"use client";

import { useCallback, useEffect, useState, useRef } from "react";
import Link from "next/link";
import { useQueryClient } from "@tanstack/react-query";
import { Avatar } from "./ui/Avatar";
import { ProfileHoverCard } from "./ProfileHoverCard";
import { profileHref } from "@/lib/profileHref";
import { AffiliationChip } from "./AffiliationChip";
import { HiddenChip } from "./HiddenChip";
import { BotBadge } from "./BotBadge";
import { BotTranscriptButton } from "./BotTranscriptButton";
import { TimeAgo, EditedLabel } from "./TimeAgo";
import { ReactionSummary } from "./ReactionSummary";
import { CommentThread } from "./CommentThread";
import { type RichTextInputHandle } from "./RichTextInput";
import { usePostReaction } from "@/lib/hooks/usePostReaction";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { useDraft } from "@/lib/hooks/useDraft";
import { useCopyToClipboard } from "@/lib/hooks/useCopyToClipboard";
import { PostEditor } from "./PostEditor";
import { useAttachments } from "@/lib/hooks/useAttachments";
import { imageAttachments, readyAttachments, videoAttachments } from "@/lib/attachments";
import { AttachedFiles } from "./AttachedFiles";
import { showPostTitle } from "@/lib/postTitle";
import { errorText } from "@/lib/errors";
import { PostActionRow } from "./PostActionRow";
import { useContentModeration } from "@/lib/hooks/useContentModeration";
import { useRemoveFailedVideo } from "@/lib/hooks/useRemoveFailedVideo";
import { ImageLightbox } from "./ImageLightbox";
import { RichContent } from "./RichContent";
import { PollDisplay } from "./PollDisplay";
import { PostJoinPrompt } from "./PostJoinPrompt";
import { PostActionMenu } from "./PostActionMenu";
import { RemoveContentDialog } from "./RemoveContentDialog";
import { PostCommentForm, COMMENT_FORM_FRAME } from "./PostCommentForm";
import type { PostCard as V1PostCard } from "@theta/api-client";
import { useComments } from "@/lib/hooks/useComments";
import { pendingLocalCount } from "@/lib/thread";
import { useCreateComment } from "@/lib/hooks/useCreateComment";
import { enterThread, useThreadFocus } from "@/lib/hooks/useThreadFocus";
import { markRead, newestStamp, useUnreadSnapshot } from "@/lib/hooks/usePostReads";
import { postKey } from "@/lib/hooks/useFeedItem";
import { groupPostsKey } from "@/lib/hooks/useGroupPage";
import { homeFeedKey } from "@/lib/hooks/useHomeFeed";

/** The image gallery runs edge-to-edge in the card, so it has to break out of
    the content block's padding — RichContent renders it inline with the body. */
const GALLERY_BLEED =
  "[&>[data-testid=rich-content-gallery]]:-mx-gutter [&>[data-testid=rich-content-gallery]]:-mb-3";

/**
 * A POST TITLE LOOKS THE SAME WHEREVER IT IS DRAWN (OSO-576).
 *
 * One string for both arms below, because the two had drifted into two
 * different-looking titles for the same object, in two different ways:
 *
 *  - **No size at all.** Neither heading carried a `text-*`, so both inherited
 *    whatever the page around the card happened to set. That is fine right up
 *    until one of those pages changes, and it is invisible when it does —
 *    nothing in this file says what size a title is. `text-base` is what both
 *    render at today; pinning it is the whole point, not a new look.
 *  - **The display face on the detail page.** `h1 { font-family: "Neureal" }`
 *    in `globals.css` is a page-heading rule, and a post title is content, not
 *    chrome — hence the Inter override, which has been on the `<h1>` since May
 *    and did nothing: the rule was UNLAYERED, and an unlayered declaration
 *    beats one in `@layer utilities` whatever its specificity. Moving that rule
 *    into `@layer base` is what makes the override here take effect, and it is
 *    why the class is on both arms rather than just the one that needs it.
 *
 * Deliberately NOT bigger on the detail page: the title is the same sentence
 * about the same post, the surrounding page already says which screen you are
 * on, and growing it is a design change nobody asked for.
 */
const POST_TITLE_CLASS =
  "text-base font-semibold text-foreground mt-2 [font-family:var(--font-inter)]";

/** Render plain text with @mentions styled inline */
function renderWithMentions(text: string) {
  const parts = text.split(/(@\S+)/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    part.startsWith("@") && part.length > 1 ? (
      <span key={i} className="text-primary-muted-foreground bg-primary-muted px-1 py-0.5 rounded font-medium">
        {part}
      </span>
    ) : (
      part
    )
  );
}

export function PostCard({
  post,
  showGroup,
  variant = "feed",
  allowAnonymousComments = false,
  highlightUnread = false,
  freshAfterMount = true,
}: {
  post: V1PostCard;
  showGroup?: boolean;
  variant?: "feed" | "detail";
  /**
   * Whether this group takes anonymous comments.
   *
   * A PROP, because `PostCard` (the v1 representation) does not carry it and
   * a card is not a group: the group page knows, the home feed does not. See
   * `blockedOn`.
   */
  allowAnonymousComments?: boolean;
  /**
   * Whether new-to-you comments light up on this card (OSO-622).
   *
   * A PROP for the same reason `allowAnonymousComments` is: the surface
   * decides, not the payload. `viewerLastSeenAt` is on every card — group
   * feeds and the detail document carry it too — but only the HOME feed opts
   * into highlights (OSO-622 §6 keeps group feeds highlight-free; gating on
   * field presence would light them all). It also gates DWELL marking: a
   * quiet home-feed card still marks — creating the `post_reads` row is what
   * arms highlights for a cold-start viewer — while group-feed cards never
   * mark at all.
   */
  highlightUnread?: boolean;
  /**
   * Whether the payload this card renders came from a fetch that COMPLETED
   * after the surrounding page mounted (OSO-622). The home feed passes
   * react-query's `isFetchedAfterMount`; while it is false the card is a warm
   * remount rendering CACHED data whose `viewerLastSeenAt` may predate marks
   * the last visit wrote — freezing the unread baseline off that would
   * re-highlight comments the viewer just read. See `useUnreadSnapshot`.
   * Defaults to true because every other surface (group feeds, the detail
   * page) never highlights, so a mount-time freeze there decides nothing.
   */
  freshAfterMount?: boolean;
}) {
  const groupSlug = post.group.slug;
  const groupId = post.group.id;
  const { currentReaction, currentReactionUrl, counts, handleReact } = usePostReaction(
    post.id,
    post.reactions
  );
  const { value: commentText, setValue: setCommentText } = useDraft(`comment:${post.id}`);
  const writeComment = (value: string) => {
    enterThread(post.id);
    setCommentText(value);
  };
  const [commentAnonymous, setCommentAnonymous] = useState(false);
  /**
   * THE THREAD IS THE QUERY, on both variants (OSO-563).
   *
   * A feed card seeds this entry with the comments its own payload carries and
   * never fetches; the post page reads the whole thread and polls it. Either
   * way there is ONE list, so a write reaches what is on screen without three
   * pieces of local state trying to stay in step with it.
   */
  /**
   * The post's own page is a live thread by the route alone; a card in a feed
   * becomes one by being written in or opened (`lib/threadFocus.ts`).
   */
  // Hoisted above `useThreadFocus` (hook order is stable either way) because
  // the dwell callback below needs to know whether anybody is logged in —
  // a logged-out mark would only be a 401 per card.
  const { data: currentUser } = useCurrentUser();
  const canMarkRead = !!currentUser;
  /**
   * MEMOISED, AND LOAD-BEARING (OSO-622): `onDwell` sits in the dwell
   * observer's dependency list, and the poll re-renders this card every tick
   * that changes anything — a fresh function each render would tear the
   * observer down before its 1 s timer ever completed. `post` itself is a
   * safe dependency: the feed hands back the same object while nothing
   * changed, and when something DID change the stamp should be recomputed.
   */
  const onDwell = useCallback(
    () => markRead(post.id, newestStamp(post), canMarkRead),
    [post, canMarkRead],
  );
  const { live: threadFocused, cardRef } = useThreadFocus(
    post.id,
    // Dwell marking only where highlights are armed — the home feed. Group
    // feeds never mark (their unread state belongs to the home-feed visit).
    highlightUnread ? { onDwell } : {},
  );
  const { data: thread } = useComments(post.id, {
    ...(variant === "detail" ? {} : { seed: post.comments }),
    live: variant === "detail" || threadFocused,
  });
  /**
   * The detail page marks on MOUNT instead of on dwell: opening the post IS
   * reading it (design 2b). Living here rather than in `SinglePostClient`
   * covers both detail routes (`/opslag/{id}` and the group-scoped one) with
   * one effect.
   *
   * THE STAMP FOLDS THE RENDERED THREAD IN. The detail payload never embeds
   * comments (`SinglePostClient` hands the card `comments: []`), so
   * `newestStamp(post)` alone would echo `post.createdAt` — reading a thread
   * here would never clear its unread count, and a cold-start viewer's first
   * mark would ARM every existing comment as unread for content they just
   * read. `thread` is in the dependency list ON PURPOSE: the effect re-runs
   * as the thread loads and as the 1 Hz poll delivers new rows, and each
   * newer stamp re-reports — reading more IS seeing more. The ledger in
   * `usePostReads` makes the repeats free, and the mark advance moving the
   * detail token is the accepted A4 trade-off.
   */
  useEffect(() => {
    if (variant !== "detail") return;
    markRead(post.id, newestStamp(post, thread ?? []), canMarkRead);
  }, [variant, post, thread, canMarkRead]);
  const queryClient = useQueryClient();
  const [showPostMenu, setShowPostMenu] = useState(false);
  const { status: copyStatus, copy } = useCopyToClipboard();
  const [deleted, setDeleted] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const mentionRef = useRef<RichTextInputHandle>(null);
  // Files attached to the comment being written. Shared with the lightbox,
  // which posts through the same mutation.
  const commentFiles = useAttachments({ type: "group", id: groupId });

  /**
   * EVERY AFFORDANCE ON THIS CARD IS `can`
   *
   * There were three checks here — `isMod` (from `current_user_role`),
   * `isMember` (the same field, read as a boolean) and `canFlag` (a formula
   * over both plus `isAuthor`) — and `current_user_role` reached this
   * component through a compat derivation because D11 took it off the wire.
   * It was taken off precisely BECAUSE recomputing capabilities from it was
   * its only use, and a client's copy of an authorisation rule is the copy
   * that disagrees with the server. `post.can` comes from the same predicate
   * the route enforces.
   */
  const can = post.can;
  /**
   * THREE ANONYMOUS CASES, AND THEY ARE READ OFF `author` BEING NULL — never
   * off a `"anonym"` sentinel in a handle (O-21). The server withholds the
   * author from anybody who may not see through the anonymity, so:
   *
   *   author is null            -> "Anonym", to everyone it is anonymous to.
   *   author is me              -> "Dig (anonymt)".
   *   author is somebody else   -> the server DE-ANONYMISED it for this
   *                                viewer (a moderator). Say "Anonym" and put
   *                                the name beside it, as the comment row
   *                                does — never the plain name, which would
   *                                make a moderator's view look like the
   *                                author had posted openly.
   */
  const isHiddenAnon = post.isAnonymous && !post.author;
  const isOwnAnon =
    post.isAnonymous && !!post.author && !!currentUser && post.author.id === currentUser.id;
  const isRevealedAnon = post.isAnonymous && !isHiddenAnon && !isOwnAnon;
  const authorName = post.author?.name ?? "Anonym";
  /**
   * THE PROFILE ADDRESS IS THE ID (ruling O-21a): `/profil/{id}`, never
   * `/profil/{username}`, because ten real accounts carry a numeric username
   * equal to a DIFFERENT user's id and a username-addressed route serves the
   * wrong person for them.
   *
   * Null when there is nobody to link to — an author whose account is gone.
   * The anonymous cases never reach it; they are branched on above.
   */
  const authorHref = profileHref(post.author?.id ?? null);
  // `can.comment` implies a signed-in member (`can` is viewer-scoped and in
  // the server-rendered HTML, OSO-673), so the ⋮-menu does not pop in when the
  // browser's first `GET /me` later confirms what the seeded read already
  // said. A signed-in NON-member with no other capability still waits for it.
  const showMenu =
    can.edit || can.delete || can.hide || can.unhide || can.flag || can.comment || !!currentUser;
  const [showJoinPrompt, setShowJoinPrompt] = useState(false);

  // ONE `attachments` ARRAY (C16/U11): the gallery and the grid variants come
  // from the same ordered list, so they cannot disagree about how many images
  // the post has — which is what `image_urls` + `image_grid_urls` could.
  // Images are narrowed to the ready ones: only a VIDEO is ever `processing`
  // (`needsProcessing` is an extension test), so a non-ready image would be a
  // row with no bytes and nothing to say about it.
  const images = readyAttachments(imageAttachments(post.attachments));
  // Videos are NOT narrowed (OSO-490): a clip that is still encoding — or one
  // that failed — is exactly what the player has to draw a placeholder or an
  // error chip for. They are not gallery cells; each gets its own player,
  // which never starts on its own (OSO-437).
  const videos = videoAttachments(post.attachments);

  /**
   * NOT EVERYBODY'S POST YET — OSO-490 §5. `publishedAt === null` means the
   * post is held back for a video, and the server hands such a row to its
   * AUTHOR ALONE. So this branch is only ever taken by the person who wrote
   * it, and the interactive parts are withheld either way: there is nobody to
   * react to it or reply to it yet.
   *
   * TWO KINDS OF HELD BACK, AND THEY DO NOT GET THE SAME CARD.
   *
   * `awaitingVideo` is a wait: the clip is encoding, the post goes out on its
   * own, and there is nothing to do. That one is DIMMED, which is what "come
   * back later" looks like.
   *
   * `blockedByFailure` is not a wait — the transcode will never finish and the
   * post will never publish until the member acts. Telling them "Udgives, når
   * videoen er klar" there would be a straight lie, and dimming the one
   * control they must find would be the worst place on the card to spend
   * contrast. So it keeps full opacity and says what is actually wrong.
   */
  const pending = post.publishedAt === null;
  const blockedByFailure =
    pending && post.attachments.some((a) => a.status === "failed");
  const awaitingVideo = pending && !blockedByFailure;

  /**
   * WITHDRAWN FROM CIRCULATION, AND ON YOUR PAGE ANYWAY — OSO-582.
   *
   * `isHidden` used to reach this component only on the detail document; the
   * feed never contained a held row, so the chip below was guarded with
   * `"isHidden" in post`. It is on `PostCard` now, and the server hands the
   * row to exactly two principals — the author, and a moderator of the group
   * — so this branch is only ever taken by somebody who is meant to see it.
   *
   * DIMMED FOR THE SAME REASON `awaitingVideo` IS, and the two can be true at
   * once: both say "this is not out there", and the card must not read as a
   * live post among live posts. The chip beside the timestamp is what says
   * WHICH of the two it is; the opacity alone would be ambiguous.
   */
  const muted = awaitingVideo || post.isHidden;

  /**
   * THE PARENT IS THE URL (C4): a top-level comment is posted to the post's
   * own id, so `postedTo` and `rootPostId` are the same number here. A reply
   * differs only in that first argument — see `CommentComposer`.
   */
  const submitCommentMutation = useCreateComment({
    rootPostId: post.id,
    postedTo: post.id,
  });

  // One submit path for the card's comment box and the lightbox's, so an image
  // attached in either place ends up on the comment.
  const submitComment = () => {
    if (commentFiles.isUploading || submitCommentMutation.isPending) return;
    const text = (mentionRef.current?.getRaw() ?? commentText).trim();
    // A comment carrying only a photo is a perfectly good comment: the files
    // travel as rows, so an empty body is no longer an empty request.
    if (!text && !commentFiles.hasContent) return;
    // The text and the tray travel as VARIABLES, read here and not inside the
    // mutation (OSO-539): the box is emptied on the next line, and a
    // `mutationFn` that reached back for them would assemble its request from
    // a tray that had just been cleared. Every picture pasted into a comment
    // was dropped in silence for exactly that reason.
    enterThread(post.id);
    // Captured before the box is emptied, so a refusal can put them back.
    const tray = commentFiles.attachments;
    const anonymous = commentAnonymous;
    submitCommentMutation.mutate(
      {
        bodyMarkdown: text,
        attachments: commentFiles.uploadIds,
        trayAttachments: tray,
        isAnonymous: anonymous,
      },
      {
        /**
         * A REFUSED COMMENT PUTS EVERYTHING BACK, not just the text.
         *
         * The tray is emptied on send so the picture appears once — on the
         * comment — rather than twice. But a write that fails after that had
         * silently dropped the attachments, and the author, handed their text
         * back, would press send again and post without them. The anonymous
         * box is the same shape and worse: unticked on send, it would post the
         * retry under their real name.
         */
        onError: () => {
          setCommentText(text);
          commentFiles.restore(tray);
          setCommentAnonymous(anonymous);
        },
      },
    );
    setCommentText("");
    commentFiles.reset();
    mentionRef.current?.clearMentions();
    setCommentAnonymous(false);
  };

  const canSubmitComment =
    (commentText.trim().length > 0 || commentFiles.hasContent) &&
    !commentFiles.isUploading &&
    !submitCommentMutation.isPending;

  const {
    isEditing,
    setIsEditing,
    editText,
    editAttachments,
    editLoading,
    startEdit,
    remove,
    hide,
    unhide,
    removal,
    removalRuleId,
    setRemovalRuleId,
    removalPending,
    confirmRemoval,
    cancelRemoval,
    showFlagForm,
    setShowFlagForm,
    flagReason,
    setFlagReason,
    flagRules,
    rulesLoading,
    flagRuleId,
    setFlagRuleId,
    flagLoading,
    flagDone,
    flag,
    actionError,
  } = useContentModeration({
    postId: post.id,
    groupId,
    subject: "Opslaget",
    // Both feeds read /api/v1 now. Their conditional polls would catch a
    // deletion on page one within the second, but somebody who has scrolled
    // into history is not polling at all, and a card that stays after being
    // deleted looks like the delete failed.
    invalidateOnRemove: [groupPostsKey(groupSlug), homeFeedKey],
    onDeleted: () => setDeleted(true),
    onHidden: () => setDeleted(true),
    // The detail page reads `postKey`, which is not in the feed list above —
    // and unlike hide, the card stays on screen, so it must be re-read to drop
    // the Skjult chip and offer Skjul instead of Vis igen.
    onUnhidden: () => queryClient.invalidateQueries({ queryKey: postKey(post.id) }),
  });

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/grupper/${groupSlug}/opslag/${post.id}`;
    // The label and its two-second reset are the hook's; what stays here is the
    // side effect only this call site has — on failure the menu is left OPEN,
    // so the "failed" label is actually on screen to read.
    if (await copy(url)) setShowPostMenu(false);
  };

  const handleStartEdit = () => {
    setShowPostMenu(false);
    startEdit();
  };

  const handleSavedEdit = () => {
    setIsEditing(false);
    queryClient.invalidateQueries({ queryKey: groupPostsKey(groupSlug) });
    queryClient.invalidateQueries({ queryKey: homeFeedKey });
    queryClient.invalidateQueries({ queryKey: postKey(post.id) });
  };

  // "Fjern video" — the same three keys an edit re-reads, because that is what
  // this is: publishing the post is a side effect of the edit (OSO-490 §2).
  const { removeFailed, removing, removeError, emptiesTheRow } =
    useRemoveFailedVideo({
      postId: post.id,
      bodyHtml: post.bodyHtml,
      attachments: post.attachments,
      invalidate: [groupPostsKey(groupSlug), homeFeedKey, postKey(post.id)],
    });

  /**
   * A moderator removing somebody else's content is the one removal the
   * server notifies the author of (OSO-663) — the dialog's notice and rule
   * picker follow the same line, drawn from `can` and nothing else (D11).
   *
   * `can.hide || can.unhide` IS `canModerate`, and `can.edit` IS `isAuthor`:
   * both verbatim from `postCapabilities`, and `edit` is the author's even on
   * their own ANONYMOUS post ("anonymity is not a disguise from yourself").
   * Comparing `post.author?.id` to the viewer instead — which this did — got
   * that case backwards: the serializer withholds `author` from EVERYONE on
   * an anonymous post, its own writer included, so a moderator removing their
   * own anonymous post was promised a notification the producer then
   * correctly suppressed, and the rule they cited was validated and dropped.
   */
  const moderatingRemoval = (can.hide || can.unhide) && !can.edit;

  const handleDelete = () => {
    setShowPostMenu(false);
    remove();
  };

  const handleHide = () => {
    setShowPostMenu(false);
    hide();
  };

  const handleUnhide = () => {
    setShowPostMenu(false);
    unhide();
  };

  const displayComments = thread ?? [];
  /**
   * The wire's count is the whole thread's and the list on screen may be only
   * a bounded preview of it, so neither alone is right — the larger is.
   *
   * THE PENDING ROWS ARE ADDED TO THE WIRE'S NUMBER, INSIDE THE MAX, and that
   * placement is the whole of it. They are already IN `displayComments`, so
   * adding them to the larger of the two counted them twice wherever the list
   * was the larger — which is every short thread: one comment plus a reply of
   * your own read "3 kommentarer" under two. Added to the wire's number
   * instead, both arms describe the same thread and the max picks the honest
   * one.
   */
  const replyCount = Math.max(
    post.replyCount + pendingLocalCount(post.id, displayComments),
    displayComments.length,
  );
  /**
   * The frozen per-pageview unread snapshot (OSO-622) — see
   * `useUnreadSnapshot` for why the latch-once freeze is the whole sticky
   * requirement, and why it waits for `freshAfterMount` on the home feed.
   *
   * OWN ACTIVITY NEEDS NO CLIENT WORK, deliberately: the server advances the
   * mark when this viewer comments (route-level hook), and client-side the
   * frozen baseline plus the snapshot's `author != viewer` exclusion keep
   * their own NAMED rows unhighlighted. The one gap is the viewer's own
   * ANONYMOUS comment — it arrives `author: null` to its author too, so the
   * exclusion cannot see it; the server never counts it, and the residual
   * same-pageview self-highlight is accepted (see `useUnreadSnapshot`). So
   * `useCreateComment` and `usePostReaction` stay untouched — do not "fix"
   * them to write the mark.
   */
  const { unreadIds, unreadCount } = useUnreadSnapshot(
    post,
    currentUser?.id ?? null,
    displayComments,
    freshAfterMount,
  );
  const showUnread = highlightUnread && unreadCount > 0;
  const hasReactions = counts.some((c) => c.count > 0);
  const postUrl = `/grupper/${groupSlug}/opslag/${post.id}`;

  /**
   * The excerpt no longer needs cleaning: a `[photo.jpeg]` remnant was a
   * markdown attachment token the server had left in the body text, and
   * attachments are not in the body any more (§1.12).
   *
   * The UUID-title guard is gone with it. `title` is `string | null` and means
   * it (C3) — the 55,049 `crypto.randomUUID()` titles that existed only
   * because a post required a topic and a topic required a title are not on
   * the v1 wire, so there is nothing to detect.
   */
  const excerptText = (post.excerpt || "").trim();
  const showTitle = showPostTitle(post);

  if (deleted) return null;

  return (
    <div
      ref={cardRef}
      className={`bg-surface rounded-xl border border-border${muted ? " opacity-60" : ""}`}
      data-pending={pending || undefined}
      data-pending-reason={pending ? (blockedByFailure ? "failed" : "processing") : undefined}
    >
      {actionError && !showFlagForm && (
        <p className="px-gutter pt-3 text-xs text-destructive">{actionError}</p>
      )}
      {removeError && <p className="px-gutter pt-3 text-xs text-destructive">{removeError}</p>}
      {/* Header + Content */}
      <div className={`px-gutter pt-4 pb-3 ${GALLERY_BLEED}`}>
        {/* The group eyebrow. It is also the whole header of a post's own page
            now (OSO-584 §5), where the group can be "Danmarks Digitale
            Frivillige - Admins og Moderatorer" — so it WRAPS rather than
            truncates, and the icon holds the first line instead of drifting to
            the middle of two. A cut-off name answers "where am I" worse than a
            second 16 px line costs. */}
        {showGroup && (
          <Link
            href={`/grupper/${groupSlug}`}
            className="flex items-start gap-1.5 text-xs text-muted-foreground hover:text-indigo-600 dark:hover:text-indigo-400 mb-2 transition-colors py-1"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mt-px shrink-0"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <span className="font-medium">{post.group.name}</span>
          </Link>
        )}
        {post.broadcast?.parent && (
          <Link
            href={`/grupper/${post.broadcast.parent.slug}`}
            className="inline-flex items-center gap-1.5 self-start rounded-full bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900 transition-colors mb-1"
            title={`Massepost fra ${post.broadcast.parent.name}`}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m3 11 18-5v12L3 14v-3z" />
              <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
            </svg>
            Fra {post.broadcast.parent.name}
          </Link>
        )}
        <div className="flex items-center gap-2.5">
          {post.isAnonymous ? (
            <div className="shrink-0 w-9 h-9 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-500 dark:text-purple-400">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="23" y1="11" x2="17" y2="11" />
              </svg>
            </div>
          ) : (
            <ProfileHoverCard userId={post.author?.id ?? null} categoryId={groupId}>
              {authorHref ? (
                <Link href={authorHref} className="shrink-0">
                  <Avatar
                    url={post.author?.avatar.medium ?? null}
                    name={authorName}
                    size="sm"
                  />
                </Link>
              ) : (
                <span className="shrink-0">
                  <Avatar
                    url={post.author?.avatar.medium ?? null}
                    name={authorName}
                    size="sm"
                  />
                </span>
              )}
            </ProfileHoverCard>
          )}
          <div className="flex-1 min-w-0 flex items-center gap-2">
            {isOwnAnon ? (
              <span className="font-semibold text-purple-700 dark:text-purple-300 text-sm">
                Dig (anonymt)
              </span>
            ) : isHiddenAnon ? (
              <span className="font-semibold text-purple-700 dark:text-purple-300 text-sm">
                Anonym
              </span>
            ) : isRevealedAnon ? (
              <span className="font-semibold text-purple-700 dark:text-purple-300 text-sm">
                Anonym
                <span className="ml-1 text-subtle-foreground font-normal" title="Kun synlig for dig">
                  ({authorName})
                </span>
              </span>
            ) : (
              <ProfileHoverCard userId={post.author?.id ?? null} categoryId={groupId}>
                {authorHref ? (
                  <Link href={authorHref} className="font-semibold text-foreground text-sm hover:underline truncate py-1">
                    {authorName}
                  </Link>
                ) : (
                  <span className="font-semibold text-foreground text-sm truncate py-1">
                    {authorName}
                  </span>
                )}
              </ProfileHoverCard>
            )}
            {post.author?.isBot && (
              <>
                <BotBadge className="shrink-0" />
                <BotTranscriptButton postId={post.id} />
              </>
            )}
            {post.authorAffiliation && (
              <AffiliationChip affiliation={post.authorAffiliation} inherited className="shrink-0" />
            )}
            <TimeAgo date={post.createdAt} />
            {/* OSO-592: beside the time, because that is the question it
                answers — what you are reading is not what was posted. */}
            <EditedLabel at={post.editedAt} />
            {/* OSO-582: the card says it too now, and the feed does serve a
                held row — to its author and to a moderator of the group. */}
            {post.isHidden && <HiddenChip />}
            {showMenu && (
              <PostActionMenu
                open={showPostMenu}
                setOpen={setShowPostMenu}
                copyStatus={copyStatus}
                onCopyLink={() => void handleCopyLink()}
                canEdit={can.edit}
                onStartEdit={handleStartEdit}
                editLoading={editLoading}
                canHide={can.hide}
                onHide={handleHide}
                canUnhide={can.unhide}
                onUnhide={handleUnhide}
                canDelete={can.delete}
                onDelete={handleDelete}
                canFlag={can.flag}
                flagDone={flagDone}
                showFlagForm={showFlagForm}
                setShowFlagForm={setShowFlagForm}
                flagReason={flagReason}
                setFlagReason={setFlagReason}
                flagRules={flagRules}
                flagRuleId={flagRuleId}
                setFlagRuleId={setFlagRuleId}
                flagLoading={flagLoading}
                onFlag={flag}
                actionError={actionError}
              />
            )}
            <RemoveContentDialog
              action={removal}
              kind="post"
              moderating={moderatingRemoval}
              rules={flagRules}
              rulesLoading={rulesLoading}
              ruleId={removalRuleId}
              setRuleId={setRemovalRuleId}
              pending={removalPending}
              error={actionError}
              onConfirm={() => void confirmRemoval()}
              onCancel={cancelRemoval}
            />
          </div>
        </div>
        {showTitle && (
          variant === "detail" ? (
            <h1 className={POST_TITLE_CLASS}>{post.title}</h1>
          ) : (
            <Link href={postUrl}>
              <h3
                className={`${POST_TITLE_CLASS} hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors`}
              >
                {post.title}
              </h3>
            </Link>
          )
        )}
        {!post.bodyHtml && excerptText && (
          <p className="text-muted-foreground text-content mt-2 leading-relaxed">
            {renderWithMentions(excerptText)}
          </p>
        )}
        {/* Body, link previews, YouTube, audio and the bounded image gallery */}
        <RichContent
          density="post"
          /* Folded in a feed, never on the post's own page (OSO-606) — you
             opened that page to read the whole thing. */
          collapsible={variant === "feed"}
          html={post.bodyHtml}
          // OSO-568: the author closed this link's card in the composer, so it
          // is not re-derived here.
          dismissedLinkPreviewUrl={post.dismissedLinkPreviewUrl}
          images={images.map((a) => a.url)}
          videos={videos.map((a) => ({
            url: a.url,
            hlsUrl: a.hlsUrl,
            filename: a.filename,
            posterUrl: a.posterUrl,
            width: a.width,
            height: a.height,
            status: a.status,
            // Only the author can be looking at a failed row at all, and
            // `can.edit` is the server's own answer for whether they may issue
            // the edit that removes it (D11) — not a guess from `authorId`.
            //
            // AND WHAT THE ACTION IS depends on what would be left. Usually a
            // post has a body and this is the edit — but `PostComposer`
            // submits on "body OR poll", so a poll with a video and no text is
            // a shape it produces, and taking the failed clip off THAT leaves
            // the empty body + no attachments that `PATCH /posts/{id}` refuses
            // (`bodyEmpty` — a poll is not an attachment). There the only way
            // out is the delete, and the button says so.
            onRemove: emptiesTheRow
              ? can.delete
                ? handleDelete
                : undefined
              : can.edit
                ? removeFailed
                : undefined,
            removeLabel: emptiesTheRow ? "Slet opslaget" : "Fjern video",
            removeBusy: removing,
          }))}
          /* `renditions` is NULL for anything that is not a raster image, so
             the grid falls back to the original rather than reading `.grid`
             off null — and a non-image never reaches this list at all. */
          gridImages={images.map((a) => a.renditions?.grid ?? a.url)}
          onImageClick={setLightboxIndex}
          categoryId={groupId}
          className="text-gray-600 dark:text-gray-300 text-content mt-2 leading-relaxed prose max-w-none break-words [&_.mention]:text-indigo-700 dark:[&_.mention]:text-indigo-300 [&_.mention]:bg-indigo-50 dark:[&_.mention]:bg-indigo-950 [&_.mention]:px-1 [&_.mention]:py-0.5 [&_.mention]:rounded [&_.mention]:font-medium [&_.mention]:no-underline hover:[&_.mention]:bg-indigo-100 dark:hover:[&_.mention]:bg-indigo-900 [&_a]:text-indigo-600 dark:[&_a]:text-indigo-400"
        />
        {/* Audio and files come off `attachments`, not out of the body: the
            composer sends rows and writes no token for `RichContent`'s strip
            pass to find (§1.12), so an MP3 or a PDF was submitted and then
            drawn by nobody. `MessageBubble` has had this since DMs got it. */}
        <AttachedFiles attachments={post.attachments} gap="mt-3" />
      </div>

      {/* Polls */}
      {post.polls.length > 0 && (
        <div className="mt-2">
          {post.polls.map((poll) => (
            <PollDisplay key={poll.id} poll={poll} postId={post.id} groupId={groupId} />
          ))}
        </div>
      )}

      {/* Reaction summary row. Nothing has happened to a post nobody has
          seen — and the counts are the PUBLISHED ones for the author too, so
          drawing an empty row here would be a row that never fills. */}
      {!pending && (hasReactions || replyCount > 0) && (
        <div className="mx-gutter py-2.5 flex items-center justify-between">
          {hasReactions ? <ReactionSummary counts={counts} postId={post.id} /> : <span />}
          {replyCount > 0 && (
            variant === "detail" ? (
              <span className="text-sm text-muted-foreground">
                {replyCount} kommentar{replyCount !== 1 ? "er" : ""}
              </span>
            ) : (
              <Link href={postUrl} className="text-sm text-muted-foreground hover:text-gray-700 dark:hover:text-gray-300 hover:underline transition-colors py-1">
                {replyCount} kommentar{replyCount !== 1 ? "er" : ""}
              </Link>
            )
          )}
        </div>
      )}

      {/* ONE LINE INSTEAD OF THE CONTROLS, while the post is held back (§5).
          React, comment and the thread are all things a reader would do, and
          there is no reader yet: this post is the author's alone until the
          worker settles it. Saying so is what stops it reading as a broken
          card — but only the encoding case may say "when the video is ready",
          because a failed transcode is never going to be ready and the post
          will sit here until the member removes the clip. */}
      {pending ? (
        <p
          className={`mx-gutter py-3 text-sm border-t border-border ${
            blockedByFailure ? "text-destructive" : "text-subtle-foreground"
          }`}
        >
          {!blockedByFailure
            ? "Udgives, når videoen er klar"
            : emptiesTheRow
              ? "Videoen kunne ikke behandles, og der er ikke andet i opslaget end den."
              : "Videoen kunne ikke behandles. Fjern den, så bliver opslaget slået op."}
        </p>
      ) : (
      <PostActionRow
        groupId={groupId}
        currentReaction={currentReaction}
        currentReactionUrl={currentReactionUrl}
        onReact={handleReact}
        onComment={() => {
          enterThread(post.id);
          mentionRef.current?.focus();
        }}
        canReact={can.react}
        canComment={can.comment}
        onBlocked={() => setShowJoinPrompt(true)}
      />
      )}

      {/* The reason line (OSO-622): a count only, never names — "who" is what
          the highlighted bubbles below answer, and a name here would leak on
          anonymous comments. The count is the STICKY `unreadCount`, so it
          never shrinks mid-view however far the server mark advances. */}
      {!pending && showUnread && (
        <p className="mx-gutter pt-2 text-xs font-semibold text-primary">
          {unreadCount} ny{unreadCount === 1 ? "" : "e"} kommentar
          {unreadCount === 1 ? "" : "er"} siden sidst
        </p>
      )}

      {/* Comments */}
      {!pending && (
      <CommentThread
        comments={displayComments}
        postId={post.id}
        groupId={groupId}
        groupSlug={groupSlug}
        currentUserId={currentUser?.id ?? null}
        fullThread
        feedPreview={variant === "feed"}
        unreadIds={showUnread ? unreadIds : undefined}
        replyCount={replyCount}
        unreadCount={unreadCount}
      />
      )}

      {/* Join prompt for non-members */}
      {showJoinPrompt && (
        <PostJoinPrompt groupSlug={groupSlug} onClose={() => setShowJoinPrompt(false)} />
      )}

      {/* Comment input. Gated on `can.comment` FIRST (OSO-675): `can` is
          viewer-scoped and arrives WITH the post — in the server-rendered
          HTML (OSO-673) — while `currentUser` waits on the browser's first
          `GET /me`. Until that answers (and through it erroring or being
          paused offline), the form's own frame holds its place with inert
          pills — the pill copies the real input's padding and type metrics,
          so the two heights cannot drift apart — and the card neither grows
          nor shrinks under the reader at hydration. `can.comment` implies a
          signed-in member, so the placeholder resolves into the form; a
          signed-out viewer has `can.comment: false` and never sees either. */}
      {!pending && can.comment && !currentUser && (
        <div aria-hidden="true" className={COMMENT_FORM_FRAME}>
          <div className="shrink-0 w-8 h-8 rounded-full bg-muted" />
          <div className="flex-1 bg-muted rounded-2xl px-4 py-2 text-content leading-normal select-none">
            {" "}
          </div>
        </div>
      )}
      {!pending && currentUser && can.comment && (
        <PostCommentForm
          mentionRef={mentionRef}
          currentUser={currentUser}
          commentAnonymous={commentAnonymous}
          setCommentAnonymous={setCommentAnonymous}
          commentText={commentText}
          setCommentText={writeComment}
          groupId={groupId}
          groupSlug={groupSlug}
          submitComment={submitComment}
          commentFiles={commentFiles}
          isSubmitting={submitCommentMutation.isPending}
          canSubmitComment={canSubmitComment}
          anonymousAllowed={allowAnonymousComments}
          error={
            submitCommentMutation.isError
              ? errorText(submitCommentMutation.error, "Kommentaren kunne ikke sendes. Prøv igen.")
              : null
          }
        />
      )}
      {/* Edit modal — text plus add/remove/reorder of the post's images */}
      {isEditing && (
        <PostEditor
          postId={post.id}
          initialText={editText}
          initialAttachments={editAttachments}
          groupId={groupId}
          groupSlug={groupSlug}
          onClose={() => setIsEditing(false)}
          onSaved={handleSavedEdit}
        />
      )}
      {/* Image lightbox */}
      {lightboxIndex !== null && images.length > 0 && (
        <ImageLightbox
          items={images}
          initialIndex={lightboxIndex}
          post={post}
          onClose={() => setLightboxIndex(null)}
          currentReaction={currentReaction}
          currentReactionUrl={currentReactionUrl}
          reactionCounts={counts}
          onReact={handleReact}
          comments={displayComments}
          replyCount={replyCount}
          currentUser={currentUser ?? null}
          commentText={commentText}
          // The lightbox composes into the same box, so typing in it is the
          // same participation.
          setCommentText={writeComment}
          onSubmitComment={submitComment}
          commentFiles={commentFiles}
          isSubmittingComment={submitCommentMutation.isPending}
          commentAnonymous={commentAnonymous}
          setCommentAnonymous={setCommentAnonymous}
          canSubmitComment={canSubmitComment}
          anonymousAllowed={allowAnonymousComments}
        />
      )}
    </div>
  );
}
